import { createContext, ReactNode, useEffect, useState } from 'react';
import { getServerDate, getSeconds, getMinutes, getHours, HOURS_MS } from './clock-provider';

interface IClockProps {
    children: ReactNode,
    localTime: number,
    saleEndDate: Date,
    speed?: number,
    textColor?: string,
    showSeconds?: boolean,
}

interface IClockContext {
    hour: string,
    minute: string,
    sec: string,
    mode: string,
    textColor?: string,
    showSeconds?: boolean,
    prefixText?:string,
    clockSeparator?:string /* 'default' or undefind: {23:59:59} | 'style-h-m-s' : {23h 59m 59s}*/
}

export const ClockContext = createContext<IClockContext>({
    hour: '',
    minute: '',
    sec: '',
    mode: 'off',
});

/*
  Description
  -----------
  Sale end date: the date/time when the sale ends.
  Server time: the real time on server.
  Client time: the browser time (defined once on page load outside).
  Max difference: if the difference is > max (from setting) or < 0 -> never show the clock.
*/

export const ClockProvider = (props: IClockProps) => {

    const [ maxDifference ] = useState(Number(document.getElementById('counter-period')?.innerText) || 72);
    const [ speed ] = useState(Number(props.speed) || 1000);
    const [ mode, setMode] = useState('off'); // 'off', 'on'

    const [ saleEndDateTimeMs ] = useState(props.saleEndDate.getTime()); // sale end date-time in milliseconds
    const [ baseServerDateTimeMs ] = useState(getServerDate()); // server date-time in milliseconds
    const [ baseBrowserDateTimeMs ] = useState(props.localTime); // browser date-time in milliseconds

    const [ hour, setHours ] = useState('');
    const [ minute, setMinutes ] = useState('');
    const [ sec, setSec ] = useState('');

    // init the clock ticks
    useEffect(() => {
        let isMounted = true;

        const handler = window.setInterval(() => {

            // https://stackoverflow.com/questions/53949393/cant-perform-a-react-state-update-on-an-unmounted-component
            if(!isMounted){
                window.clearInterval(handler);
                return;
            }

            // take the number of time till the end of the sale in server time that was calculated on page load.
            // every second decrease this value
            const currentBrowserTime = (new Date()).getTime();
            const diff = saleEndDateTimeMs - baseServerDateTimeMs - (currentBrowserTime - baseBrowserDateTimeMs);

            // calculate the number of hours until the end of the sale
            const hoursTillEndOfDiscount = diff / HOURS_MS;

            // if these hours > max or < 0 ---> stop
            if(hoursTillEndOfDiscount <= 0 || hoursTillEndOfDiscount >= maxDifference) {
                // stop then clock
                setMode(newMode => 'off');
                setHours('');
                setMinutes('');
                setSec('');

                window.clearInterval(handler);
                return;
            }

            // update the clock UI
            setMode(newMode => 'on');
            setHours(getHours(diff));
            setMinutes(getMinutes(diff));
            setSec(getSeconds(diff));

        }, speed);

        return () => {
            isMounted = false
        };
    }, [baseBrowserDateTimeMs, baseServerDateTimeMs, maxDifference, saleEndDateTimeMs, speed]);

    return (
        <ClockContext.Provider value={{
            ...props, mode, hour, minute, sec,
        }}>
            { props.children }
        </ClockContext.Provider>
    )
};
